import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { nanoid } from "nanoid";
import { Form, Input, Button, Drawer, Typography, notification } from "antd";
import { CloseOutlined, LinkOutlined } from "@ant-design/icons";
import { Tokens } from "Shared/Tokens";
import { addWallet, setWalletBalanceCache } from "store/Actions/wallets";
import {
    isValidWalletAddress,
    fetchOnChainWallet,
    detectBrowserWalletAddress,
} from "utils/Wallets";

const { Title, Text } = Typography;

const CHAINS = [
    {
        id: "solana",
        label: "Solana",
        badge: "SOL",
        placeholder: "7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU",
        suggestedNames: ["Phantom", "Solflare", "Cold SOL"],
    },
    {
        id: "ethereum",
        label: "Ethereum",
        badge: "ETH",
        placeholder: "0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
        suggestedNames: ["MetaMask", "Ledger ETH", "Main EVM"],
    },
];

const AddWalletDrawer = (props) => {
    const {
        open,
        onClose,
        defaultChain = "solana",
        currency = "USD",
        onWalletAdded,
    } = props;
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [chain, setChain] = useState(
        defaultChain === "ethereum" ? "ethereum" : "solana"
    );
    const [nameValue, setNameValue] = useState("");
    const [addressValue, setAddressValue] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [detecting, setDetecting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (open) {
            setChain(defaultChain === "ethereum" ? "ethereum" : "solana");
            setErrorMessage("");
        }
    }, [open, defaultChain]);

    const activeChainMeta = CHAINS.find((c) => c.id === chain) || CHAINS[0];

    const isAddressValid = isValidWalletAddress(chain, addressValue);
    const canSubmit =
        Boolean(addressValue.trim()) && isAddressValid && !submitting;

    const handleSelectChain = (nextChain) => {
        setChain(nextChain);
        setErrorMessage("");
    };

    const handleSuggestionClick = (label) => {
        setNameValue(label);
        form.setFieldsValue({ walletName: label });
    };

    const handleDetectExtension = async () => {
        setDetecting(true);
        setErrorMessage("");
        try {
            const detected = await detectBrowserWalletAddress(chain);
            if (detected) {
                setAddressValue(detected);
                form.setFieldsValue({ address: detected });
                if (!nameValue.trim()) {
                    const defaultLabel =
                        chain === "solana" ? "Phantom" : "MetaMask";
                    setNameValue(defaultLabel);
                    form.setFieldsValue({ walletName: defaultLabel });
                }
            } else {
                setErrorMessage(
                    chain === "solana"
                        ? "No Solana browser wallet detected. Paste your public address below"
                        : "No Ethereum browser wallet detected. Paste your public address below"
                );
            }
        } catch (err) {
            setErrorMessage(
                "Could not read address from wallet extension. Paste your public address below"
            );
        } finally {
            setDetecting(false);
        }
    };

    const handleFinish = async () => {
        const cleanAddress = String(addressValue || "").trim();
        if (!isValidWalletAddress(chain, cleanAddress)) {
            setErrorMessage(
                chain === "solana"
                    ? "Enter a valid Solana base58 address (32–44 characters)"
                    : "Enter a valid Ethereum 0x address (42 characters)"
            );
            return;
        }

        const cleanName =
            String(nameValue || "").trim() || `${activeChainMeta.label} wallet`;

        setSubmitting(true);
        setErrorMessage("");

        const walletEntry = {
            uid: nanoid(),
            name: cleanName,
            chain,
            address: cleanAddress,
            addedOn: Date.now(),
        };

        dispatch(addWallet(walletEntry));

        try {
            const liveData = await fetchOnChainWallet(chain, cleanAddress, {
                currency,
            });
            if (liveData) {
                dispatch(setWalletBalanceCache(chain, cleanAddress, liveData));
            }
        } catch (e) {
            notification.warning({
                message: "Wallet saved",
                description:
                    "Address added. Live on-chain balances will sync when the network responds.",
                key: "wallet-sync-warning",
            });
        } finally {
            setSubmitting(false);
            setNameValue("");
            setAddressValue("");
            form.resetFields();
            if (typeof onWalletAdded === "function") {
                onWalletAdded(walletEntry);
            }
            if (typeof onClose === "function") {
                onClose();
            }
        }
    };

    return (
        <Drawer
            title={
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    <Title
                        level={4}
                        style={{
                            margin: 0,
                            color: Tokens.textPrimary,
                            fontWeight: 500,
                            fontSize: "18px",
                        }}
                    >
                        Add wallet
                    </Title>
                </div>
            }
            width={isMobile ? "100%" : 460}
            height={isMobile ? "82%" : "100%"}
            placement={isMobile ? "bottom" : "right"}
            push={false}
            closable
            closeIcon={
                <CloseOutlined style={{ color: Tokens.textSecondary }} />
            }
            onClose={() => onClose && onClose()}
            open={open}
            maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
            headerStyle={{
                backgroundColor: Tokens.bg,
                borderBottom: `1px solid ${Tokens.hairline}`,
            }}
            bodyStyle={{
                backgroundColor: Tokens.bg,
                padding: "24px 16px",
            }}
        >
            <div
                data-testid="add-wallet-drawer-content"
                style={{
                    maxWidth: Tokens.containerMaxWidth,
                    margin: "0 auto",
                    width: "100%",
                    backgroundColor: Tokens.card,
                    border: `1px solid ${Tokens.hairline}`,
                    borderRadius: Tokens.radiusCard,
                    padding: "20px",
                }}
            >
                <div style={{ marginBottom: "16px" }}>
                    <Text
                        style={{
                            display: "block",
                            color: Tokens.textSecondary,
                            fontSize: "13px",
                            marginBottom: "8px",
                        }}
                    >
                        Network
                    </Text>
                    <div
                        data-testid="wallet-chain-selector"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "8px",
                        }}
                    >
                        {CHAINS.map((item) => {
                            const active = item.id === chain;
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    data-testid={`select-chain-${item.id}`}
                                    onClick={() => handleSelectChain(item.id)}
                                    style={{
                                        height: "40px",
                                        padding: "0 12px",
                                        borderRadius: Tokens.radiusControl,
                                        border: `1px solid ${
                                            active
                                                ? Tokens.accentBorder
                                                : Tokens.hairline
                                        }`,
                                        backgroundColor: active
                                            ? Tokens.accentSubtle
                                            : Tokens.raised,
                                        color: active
                                            ? Tokens.textPrimary
                                            : Tokens.textSecondary,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        cursor: "pointer",
                                        fontFamily: Tokens.fontSans,
                                        fontSize: "14px",
                                        fontWeight: active ? 500 : 400,
                                    }}
                                >
                                    <span>{item.label}</span>
                                    <span
                                        style={{
                                            fontFamily: Tokens.fontMono,
                                            fontSize: "11px",
                                            color: active
                                                ? Tokens.accent
                                                : Tokens.textMuted,
                                        }}
                                    >
                                        {item.badge}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                    name="add-onchain-wallet"
                    autoComplete="off"
                >
                    <Form.Item
                        label={
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "13px",
                                }}
                            >
                                Label
                            </Text>
                        }
                        name="walletName"
                        style={{ marginBottom: "8px" }}
                    >
                        <Input
                            data-testid="wallet-name-input"
                            value={nameValue}
                            onChange={(e) => setNameValue(e.target.value)}
                            placeholder={`${activeChainMeta.label} wallet`}
                            style={{
                                backgroundColor: Tokens.raised,
                                borderColor: Tokens.hairline,
                                color: Tokens.textPrimary,
                                borderRadius: Tokens.radiusControl,
                                height: "40px",
                            }}
                        />
                    </Form.Item>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                            marginBottom: "16px",
                        }}
                    >
                        <span
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "12px",
                            }}
                        >
                            Presets
                        </span>
                        {activeChainMeta.suggestedNames.map((suggestion) => {
                            const selected = nameValue === suggestion;
                            return (
                                <button
                                    key={suggestion}
                                    type="button"
                                    data-testid={`wallet-preset-${suggestion
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                    onClick={() =>
                                        handleSuggestionClick(suggestion)
                                    }
                                    style={{
                                        cursor: "pointer",
                                        backgroundColor: selected
                                            ? Tokens.accentSubtle
                                            : Tokens.raised,
                                        color: selected
                                            ? Tokens.accent
                                            : Tokens.textSecondary,
                                        border: `1px solid ${
                                            selected
                                                ? Tokens.accentBorder
                                                : Tokens.hairline
                                        }`,
                                        borderRadius: Tokens.radiusTag,
                                        fontSize: "12px",
                                        padding: "2px 8px",
                                        height: "24px",
                                        lineHeight: "18px",
                                    }}
                                >
                                    {suggestion}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "8px",
                        }}
                    >
                        <Text
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "13px",
                            }}
                        >
                            Public address
                        </Text>
                        <button
                            type="button"
                            data-testid="detect-browser-wallet-btn"
                            onClick={handleDetectExtension}
                            disabled={detecting}
                            style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                color: Tokens.accent,
                                fontSize: "12px",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                            }}
                        >
                            <LinkOutlined style={{ fontSize: "12px" }} />
                            <span>
                                {detecting
                                    ? "Detecting"
                                    : "Use browser extension"}
                            </span>
                        </button>
                    </div>

                    <Form.Item name="address" style={{ marginBottom: "12px" }}>
                        <Input
                            data-testid="wallet-address-input"
                            value={addressValue}
                            onChange={(e) => {
                                setAddressValue(e.target.value);
                                setErrorMessage("");
                            }}
                            placeholder={activeChainMeta.placeholder}
                            style={{
                                backgroundColor: Tokens.raised,
                                borderColor:
                                    addressValue.trim() && !isAddressValid
                                        ? Tokens.negative
                                        : Tokens.hairline,
                                color: Tokens.textPrimary,
                                borderRadius: Tokens.radiusControl,
                                fontFamily: Tokens.fontMono,
                                fontSize: "13px",
                                height: "40px",
                            }}
                        />
                    </Form.Item>

                    {addressValue.trim() &&
                        !isAddressValid &&
                        !errorMessage && (
                            <div
                                data-testid="wallet-address-validation-hint"
                                style={{
                                    color: Tokens.negative,
                                    fontSize: "12px",
                                    marginBottom: "12px",
                                }}
                            >
                                {chain === "solana"
                                    ? "Enter a valid Solana base58 address (32–44 characters)"
                                    : "Enter a valid Ethereum 0x address (42 characters)"}
                            </div>
                        )}

                    {errorMessage && (
                        <div
                            data-testid="wallet-form-error"
                            style={{
                                color: Tokens.negative,
                                fontSize: "12px",
                                marginBottom: "12px",
                            }}
                        >
                            {errorMessage}
                        </div>
                    )}

                    <div
                        style={{
                            color: Tokens.textMuted,
                            fontSize: "12px",
                            lineHeight: "18px",
                            marginBottom: "16px",
                        }}
                    >
                        Read-only public address watching. Private keys are
                        never requested or stored
                    </div>

                    <Button
                        data-testid="submit-add-wallet-btn"
                        disabled={!canSubmit}
                        loading={submitting}
                        type="primary"
                        htmlType="submit"
                        block
                        style={{
                            borderRadius: Tokens.radiusControl,
                            fontWeight: 500,
                            height: "40px",
                        }}
                    >
                        Add {activeChainMeta.label} wallet
                    </Button>
                </Form>
            </div>
        </Drawer>
    );
};

export default AddWalletDrawer;
